import type { TAssetTypeCode } from '@alfa-bank/site-invest-emitents-common/src/models';
import {
    getProfitabilityByPeriod,
    getSymbolForPrice,
    isBond,
} from '@alfa-bank/site-invest-emitents-common/helpers';
import type { TKind } from '@alfa-bank/site-text-v2/src/models';
import { getTrendStatus } from '@alfa-bank/site-invest-common/helpers/utils/get-trend-status';
import { TREND_SYMBOLS } from '@alfa-bank/site-invest-common/src/constants';

import type { TRatingDynamic } from '../models/common';
import type {
    IAsset,
    TBaseUrlToAssetConfig,
    IScore,
    TScoreTypeNames,
    TScoreClassNames,
    IProfitability,
    IQuotationAsset,
} from '../models';
import {
    BASE_URL_TO_ASSET_SUBSTRING,
    MAX_SCORE_VALUE,
    MIN_SCORE_VALUE,
    PROFITABILITY_TYPE_DAY,
    PROFITABILITY_TYPE_YTM,
    TOTAL_PREVIOUS_SCORE_TYPE_NAME,
    TOTAL_SCORE_TYPE_NAME,
} from '../constants';

export const truncateString = (stringToTruncate: string, maxLength: number) => {
    if (maxLength <= 0) {
        throw new Error();
    }

    return stringToTruncate.length < maxLength
        ? stringToTruncate
        : stringToTruncate.substring(0, maxLength) + '...';
};

/** Формирует ссылку на страничку инструмента */
export const createLinkForPage = (
    baseUrlToAsset: TBaseUrlToAssetConfig,
    asset: Pick<IAsset, 'assetType' | 'ISIN' | 'ticker'>
) => {
    const { ISIN, assetType, ticker } = asset;
    const templatePageUrl = baseUrlToAsset[assetType.code];

    if (!templatePageUrl) {
        return '';
    }

    const linkForPage = templatePageUrl
        .replace(BASE_URL_TO_ASSET_SUBSTRING.ISIN, ISIN)
        .replace(BASE_URL_TO_ASSET_SUBSTRING.TICKER, ticker);

    return `${linkForPage}/`;
};

/** Ищет среди всех оценок - оценку заданного типа */
export const findScore = (scoreTypeName: TScoreTypeNames, scores?: IScore[]): IScore | undefined =>
    scores?.find(score => score.typeName === scoreTypeName);

/** Возвращает имя класс для добавления цвета оценок и пентагона*/
export const getScoreColorClassName = (
    value: IScore['value'],
    minNormalValue: number,
    maxNormalValue: number
): TScoreClassNames => {
    if (value === 0) {
        return 'scoreDefault';
    } else if (value < minNormalValue) {
        return 'scoreNegative';
    } else if (value >= maxNormalValue) {
        return 'scorePositive';
    }

    return 'scoreNormal';
};
/** Формирует ссылку на шаблонизирующую страницу */
export const createPageUrl = (baseUrlTo?: string | null, path?: string | null): string => {
    if (typeof path !== 'string' || typeof baseUrlTo !== 'string') {
        return '';
    }

    const normalizedBaseUrlTo = baseUrlTo.replace(/\/$/, ''); // удалить слеш в конце, если есть
    const normalizedPath = path.replace(/^\/|\/$/g, ''); // удалить слеш в начале и конце, если есть

    return `${normalizedBaseUrlTo}/${normalizedPath}/`;
};

/** Получает ISIN/ticker из переданной части url от шаблонизатора */
export const getUniqueIdFromUrl = (str?: string) =>
    str?.split('/').filter(Boolean).reverse()[0].toUpperCase();

/**
 * Возвращает число, округленное в большую сторону до указанного количества знаков после запятой.
 *
 * @param number - Число, которое нужно округлить.
 * @param numberAfterComma - Количество знаков после запятой, до которого нужно округлить число.
 */
export const roundNumber = (number: number, numberAfterComma: number) => {
    const multiplier = 10 ** Math.abs(numberAfterComma);

    return Math.ceil(number * multiplier) / multiplier;
};

// TODO в invest-common есть похожая функция, только не испольщующая TKind (и цвета чуть другие)
// засинкаться фронтами и дизайнерами, что бы придти к использованию общих цветов и перейти на общую
/**
 * Возвращает kind для Text, в зависимости от значения profitabilityPercent
 *
 * @param assetType - Тип инструмента
 * @param profitabilities - Массив доходности
 */

export const getProfitabilityTextKind = (
    assetType: TAssetTypeCode,
    profitabilities?: IProfitability[] | null
): TKind => {
    const isBondAsset = isBond(assetType);

    const { profitability, profitabilityPercent } = getProfitabilityByPeriod(
        isBondAsset ? PROFITABILITY_TYPE_YTM : PROFITABILITY_TYPE_DAY,
        profitabilities ?? []
    );

    const comparisonValue = profitabilityPercent || profitability;

    if (typeof comparisonValue === 'number') {
        if (comparisonValue > 0) {
            return 'positive';
        }
        if (comparisonValue < 0) {
            return 'negative';
        }
        if (comparisonValue === 0) {
            return 'secondary';
        }
    }

    return 'secondary';
};

/**
 * Возвращает число, округленное в большую сторону до двух знаков после запятой с символом
 *
 * @param value - Число
 * @param symbol - Символ (%, $)
 */
export const formatValueWithSymbol = (value: number | null, symbol: string) =>
    // Нужен неразрывный пробел
    // eslint-disable-next-line no-irregular-whitespace
    value === null ? null : `${roundNumber(value, 2)}${symbol}`;

interface IGetPriceParams {
    assetType: TAssetTypeCode;
    currencySymbol: string;
    lastPrice: number | null;
}

/**
 * Возвращает отформатировнную цену
 *
 * @param lastPrice - Последняя цена актива
 * @param assetType - Тип инструмента
 * @param currencySymbol - Символ валюты
 *
 * @returns Последняя цена актива, отформатированная с символом валюты или процентом (для облигаций)
 */
export const getPrice = ({ assetType, currencySymbol, lastPrice }: IGetPriceParams) => {
    const priceSymbol = getSymbolForPrice({ symbol: currencySymbol, assetType });

    return formatValueWithSymbol(lastPrice, isBond(assetType) ? priceSymbol : ` ${priceSymbol}`);
};

interface IGetProfitabilityParams {
    assetType: TAssetTypeCode;
    currencySymbol: string;
    profitabilities?: IProfitability[] | null;
}

/**
 * Возвращает доходность актива
 *
 * @param assetType - Тип инструмента
 * @param currencySymbol - Символ валюты
 * @param profitabilities - Массив с данными о доходности
 *
 * @returns Объект с отформатированными значениями доходности:
 *   - profitability: Доходность в абсолютном значении, отформатированная с символом валюты. Для облигаций всегда null
 *   - profitabilityPercent: Доходность в процентах, отформатированная с символом процента и знаком + или - перед значением. Для облигаций всегда с %
 */
export const getProfitability = ({
    assetType,
    currencySymbol,
    profitabilities,
}: IGetProfitabilityParams) => {
    const priceSymbol = getSymbolForPrice({ symbol: currencySymbol, assetType });
    const isBondAsset = isBond(assetType);

    const { profitability, profitabilityPercent } = getProfitabilityByPeriod(
        isBondAsset ? PROFITABILITY_TYPE_YTM : PROFITABILITY_TYPE_DAY,
        profitabilities ?? []
    );

    const trend = getTrendStatus(profitabilityPercent);

    const percent = profitabilityPercent
        ? `${TREND_SYMBOLS[trend]}${formatValueWithSymbol(profitabilityPercent, '%')?.replace(
              '-',
              ''
          )}`
        : null;

    const formattedProfitability = formatValueWithSymbol(profitability, `\u00A0${priceSymbol}`);
    const profitabilityValue = percent ? `(${formattedProfitability})` : formattedProfitability;

    return {
        profitability: isBondAsset ? null : profitabilityValue?.replace('-', ''),
        profitabilityPercent: percent,
    };
};

/**
 * Объединяющая функция для getProfitability и getPrice
 */
export const getProfitabilityAndPrice = ({
    assetType,
    currencySymbol,
    lastPrice,
    profitabilities,
}: IGetPriceParams & IGetProfitabilityParams) => {
    return {
        price: getPrice({ assetType, currencySymbol, lastPrice }),
        ...getProfitability({ assetType, currencySymbol, profitabilities }),
    };
};

/** Возвращает тип динамики изменения рейтинга */
export const getRatingDynamicType = (
    currentRating: number,
    previousRating: number
): TRatingDynamic => {
    if (currentRating > previousRating) {
        return 'increased';
    } else if (currentRating < previousRating) {
        return 'decreased';
    }

    return 'unchanged';
};

/** Возвращает динамику рейтинга на основе сравнения текущего и предыдущего значений total*/
export const getRatingDynamicFromCurrentAndPrevious = (scores?: IScore[]): TRatingDynamic => {
    const currentRatingValue = findScore(TOTAL_SCORE_TYPE_NAME, scores)?.value;
    const previousRatingValue = findScore(TOTAL_PREVIOUS_SCORE_TYPE_NAME, scores)?.value;

    if (currentRatingValue && previousRatingValue) {
        return getRatingDynamicType(currentRatingValue, previousRatingValue);
    }

    return 'unchanged';
};

/** Возвращает оценки заданных типов */
export const getScores = (scoreTypeNames: TScoreTypeNames[], scores?: IScore[]): IScore[] =>
    scoreTypeNames.map(typeName => findScore(typeName, scores)).filter(Boolean) as IScore[];

/** Проверяет, ялвяется ли значение score не допустимым */
export const isScoreValueNotValid = (scoreValue: number) => {
    return scoreValue <= MIN_SCORE_VALUE || scoreValue > MAX_SCORE_VALUE;
};

/**
 * Возвращает ключ для класса стилей отвечающего за цвет градусника, на основе числа.
 *
 * @param value - Число, определяющее тему.
 * @returns Строки: "red", "yellow" или "green".
 */
export const getTheme = (value: number) => {
    if (value < 5) {
        return 'red';
    } else if (value < 7) {
        return 'yellow';
    } else {
        return 'green';
    }
};

/**
 * Проверяет, нужно ли отображать эмитент на основании scores
 */
export const isShowEmitentBaseOnScores = (scores?: IScore[]): boolean => {
    const totalScoreValue = findScore(TOTAL_SCORE_TYPE_NAME, scores)?.value;

    return !!totalScoreValue && !isScoreValueNotValid(totalScoreValue);
};

export const createFilterISIN = (assets: IQuotationAsset[]) => {
    if (assets.length === 0) {
        return '{in: ["null"]}';
    }

    const filterISIN: string[] = [];

    assets.forEach((asset: IQuotationAsset) => {
        filterISIN.push(`"${asset.isin}"`);
    });

    return `{in: [${filterISIN.join(', ')}]}`;
};
